import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import PointAwardFloat from '../../stargarden/PointAwardFloat';
import { SSE_HTTP_FALLBACK_INTERVAL_MS } from '../../../live/sseHttpFallbackPolicy';

export default function LibraryPointAnimations({
  loadEvents, onAwards, boardLive = false, classLive = false, classLiveConnected = false,
}) {
  const [events, setEvents] = useState([]);
  const onAwardsRef = useRef(onAwards);
  onAwardsRef.current = onAwards;

  useEffect(() => {
    setEvents([]);
    if (!loadEvents) return undefined;
    let active = true;
    let cursor = null;
    let generation = 0;
    let timer;
    const removals = new Set();
    const seen = new Set();
    const liveMode = boardLive || classLive;
    // A connection can become ready before this component's effect subscribes
    // to browser events. The parent passes its current live state as a prop.
    let liveConnected = Boolean(classLive && classLiveConnected);
    const handleLiveStatus = (e) => {
      if (!liveMode) return;
      liveConnected = Boolean(e.detail?.connected);
      if (!liveConnected) cursor = null;
    };
    const handleLiveReward = (e) => {
      if (!liveMode || !liveConnected || !active) return;
      const reward = e.detail;
      const id = Number(reward?.id);
      const expiry = Date.parse(reward?.expires_at);
      if (!Number.isSafeInteger(id) || id <= 0 || seen.has(id)
        || !Number.isFinite(expiry) || expiry <= Date.now()) return;
      seen.add(id);
      const fresh = [{ ...reward, id }];
      setEvents((current) => [...current, ...fresh]);
      Promise.resolve(onAwardsRef.current?.(fresh)).catch(() => {});
      const removal = setTimeout(() => {
        removals.delete(removal);
        if (active) setEvents((current) => current.filter((item) => item.id !== id));
      }, 1400);
      removals.add(removal);
    };
    const handleLiveResync = () => {
      if (!active || !classLive) return;
      // SSE overflow can drop transient +5 events: reload canonical points
      // without replaying expired animations or touching a teacher PIN session.
      Promise.resolve(onAwardsRef.current?.([])).catch(() => {});
    };
    if (boardLive) {
      window.addEventListener('dandu:board-live:status', handleLiveStatus);
      window.addEventListener('dandu:board-live:reward', handleLiveReward);
    }
    if (classLive) {
      window.addEventListener('dandu:class-live:status', handleLiveStatus);
      window.addEventListener('dandu:class-live:reward', handleLiveReward);
      window.addEventListener('dandu:class-live:resync', handleLiveResync);
    }
    const reset = () => { cursor = null; generation += 1; };
    const poll = async () => {
      if (!active) return;
      if (document.hidden || navigator.onLine === false) {
        cursor = null;
      } else if (!liveConnected) {
        try {
          const previous = cursor;
          const requestGeneration = generation;
          const payload = await loadEvents(cursor);
          if (!active) return;
          cursor = requestGeneration === generation ? Math.max(Number(payload.cursor) || 0, previous || 0) : null;
          // A fresh board/refresh/reconnection starts at the current cursor.
          // It never replays events that happened before it was listening.
          if (previous !== null && cursor !== null) {
            const serverNow = Date.parse(payload.server_time) || Date.now();
            const fresh = (payload.events || []).filter((event) => Number(event.id) > previous
              && Date.parse(event.expires_at) > serverNow);
            if (fresh.length) {
              setEvents((current) => [...current, ...fresh]);
              Promise.resolve(onAwardsRef.current?.(fresh)).catch(() => {});
              const removal = setTimeout(() => {
                removals.delete(removal);
                if (active) setEvents((current) => current.filter((event) => !fresh.some((item) => item.id === event.id)));
              }, 1400);
              removals.add(removal);
            }
          }
        } catch (_) {
          cursor = null;
        }
      }
      if (active) timer = setTimeout(poll, SSE_HTTP_FALLBACK_INTERVAL_MS);
    };
    document.addEventListener('visibilitychange', reset);
    window.addEventListener('offline', reset);
    poll();
    return () => {
      active = false;
      clearTimeout(timer);
      removals.forEach(clearTimeout);
      document.removeEventListener('visibilitychange', reset);
      window.removeEventListener('offline', reset);
      window.removeEventListener('dandu:board-live:status', handleLiveStatus);
      window.removeEventListener('dandu:board-live:reward', handleLiveReward);
      window.removeEventListener('dandu:class-live:status', handleLiveStatus);
      window.removeEventListener('dandu:class-live:reward', handleLiveReward);
      window.removeEventListener('dandu:class-live:resync', handleLiveResync);
    };
  }, [loadEvents, boardLive, classLive, classLiveConnected]);

  return <Box aria-label="Kitap puanları" sx={{ position: 'fixed', bottom: boardLive ? 170 : 90, right: 30, zIndex: 1500, pointerEvents: 'none' }}>
    {events.map((event, index) => <Box key={event.id} data-testid="library-point-gain"
      sx={{ position: 'absolute', right: (index % 4) * 110, bottom: Math.floor(index / 4) * 55 }}>
      <PointAwardFloat amount={event.amount} icon="📚" />
    </Box>)}
  </Box>;
}
